import type { Module } from "vuex/types/index.d.ts";
import { $api } from "@/services/api";
import store from "@/store";
import type { PaymentCalculationViewModel, SoftPullResultViewModel } from "@/models/viewModels";
import useFormatter from "@/use/formatter";
import useArrayHelpers from "@/use/arrayHelpers";

const { formatNumber } = useFormatter();
const { groupBy } = useArrayHelpers();

const showMyRatesModule: Module<any, any> = {
  namespaced: true,
  state: {
    gettingEstimatedPayments: false as boolean,
    calculatingEstimatedPayments: false as boolean,
    estimatedLoanAmount: 0 as number,
    estimatedLoanAmountInput: 0 as number,
    calculatedPaymentOptions: [] as PaymentCalculationViewModel[],
    sampleCalculatedPaymentOptions: [] as PaymentCalculationViewModel[],
    currentApplicantTypeId: 0 as number,
    currentApplicantTypeName: "" as string,
    primaryApplicantSelectedCreditScoreRange: {},
    coApplicantSelectedCreditScoreRange: {},
    selectedCreditScoreRange: {},
    availablePricingTiers: [],
    softPullRatePlans: {} as any,
    primaryApplicantSoftPullRatePlans: [] as any[],
    coApplicantSoftPullRatePlans: [] as any[],
    currentSubpageId: "" as string,
    softPullResult: {} as SoftPullResultViewModel,
  },
  mutations: {
    SET_AVAILABLE_PRICING_TIERS(state: any, pricingTiers: any) {
      state.availablePricingTiers = pricingTiers;
    },
    SET_SOFT_PULL_RATE_PLANS(state: any, ratePlans: any) {
      state.softPullRatePlans = ratePlans;
    },
    SET_PRIMARY_APPLICANT_SOFT_PULL_RATE_PLANS(state: any, ratePlans: any) {
      state.primaryApplicantSoftPullRatePlans = ratePlans;
    },
    SET_CO_APPLICANT_SOFT_PULL_RATE_PLANS(state: any, ratePlans: any) {
      state.coApplicantSoftPullRatePlans = ratePlans;
    },
    SET_CURRENT_APPLICANT_TYPE_ID(state: any, currentApplicantTypeId: number) {
      state.currentApplicantTypeId = currentApplicantTypeId;
    },
    SET_CURRENT_APPLICANT_TYPE_NAME(state: any, currentApplicantTypeName: string) {
      state.currentApplicantTypeName = currentApplicantTypeName;
    },
    SET_GETTING_ESTIMATED_PAYMENTS(state: any, flag: boolean) {
      state.gettingEstimatedPayments = flag;
    },
    SET_CALCULATING_ESTIMATED_PAYMENTS(state: any, flag: boolean) {
      state.calculatingEstimatedPayments = flag;
    },
    SET_ESTIMATED_LOAN_AMOUNT(state: any, amount: number) {
      state.estimatedLoanAmount = amount;
    },
    SET_ESTIMATED_LOAN_AMOUNT_INPUT(state: any, amount: number) {
      const number = typeof amount === "string" ? parseInt(amount) : amount;
      state.estimatedLoanAmountInput = number;
    },
    SET_CALCULATED_PAYMENT_OPTIONS(state: any, calculatedPaymentOptions: PaymentCalculationViewModel[]) {
      state.calculatedPaymentOptions = calculatedPaymentOptions;
    },
    SET_SAMPLE_CALCULATED_PAYMENT_OPTIONS(state: any, calculatedPaymentOptions: PaymentCalculationViewModel[]) {
      state.sampleCalculatedPaymentOptions = calculatedPaymentOptions;
    },
    SET_CURRENT_SUBPAGE_ID(state: any, subpageId: string) {
      state.currentSubpageId = subpageId;
    },
    SET_SOFT_PULL_RESULT(state: any, softPullResult: SoftPullResultViewModel) {
      state.softPullResult = softPullResult;
    },
  },
  actions: {
    setCalculatingEstimatedPayments({ commit }, payload) {
      commit("SET_CALCULATING_ESTIMATED_PAYMENTS", payload);
    },
    setGettingEstimatedPayments({ commit }, payload) {
      commit("SET_GETTING_ESTIMATED_PAYMENTS", payload);
    },
    setCalculatedPaymentOptions({ commit }, payload) {
      commit("SET_CALCULATED_PAYMENT_OPTIONS", payload);
    },
    setSampleCalculatedPaymentOptions({ commit }, payload) {
      commit("SET_SAMPLE_CALCULATED_PAYMENT_OPTIONS", payload);
    },
    setEstimatedLoanAmount({ commit }, payload) {
      commit("SET_ESTIMATED_LOAN_AMOUNT", payload);
    },
    setCurrentApplicantTypeId({ commit }, payload) {
      commit("SET_CURRENT_APPLICANT_TYPE_ID", payload);
    },
    setCurrentSubpageId({ commit }, payload) {
      commit("SET_CURRENT_SUBPAGE_ID", payload);
    },
    setSoftPullResult({ commit }, payload) {
      commit("SET_SOFT_PULL_RESULT", payload);
    },
    async calculateSamplePayments({ commit, dispatch }, payload: any) {
      dispatch("setCalculatingEstimatedPayments", true);

      const request = {
        applicantId: payload?.applicantId,
        estimatedLoanAmount: payload?.asdAmtFinanced,
      };

      const payments = await $api.refi.calculatePayments(payload?.applicantId, request);

      if (payments?.length > 0 && !payments?.error) {
        const sortedPaymentsAsc = payments?.sort(
          (a: PaymentCalculationViewModel, b: PaymentCalculationViewModel) =>
            parseInt(a?.termYears) - parseInt(b?.termYears),
        );
        commit("SET_SAMPLE_CALCULATED_PAYMENT_OPTIONS", sortedPaymentsAsc);
      } else {
        commit("SET_SAMPLE_CALCULATED_PAYMENT_OPTIONS", []);
      }

      dispatch("setCalculatingEstimatedPayments", false);
    },
    async calculatePayments({ state, commit, dispatch }, applicantId: number) {
      dispatch("setCalculatingEstimatedPayments", true);

      const request = {
        applicantId: applicantId,
        estimatedLoanAmount: state.estimatedLoanAmount,
      };

      const payments = await $api.refi.calculatePayments(applicantId, request);

      if (payments?.length > 0 && !payments?.error) {
        const sortedPaymentsAsc = payments?.sort(
          (a: PaymentCalculationViewModel, b: PaymentCalculationViewModel) =>
            parseInt(a?.termYears) - parseInt(b?.termYears),
        );
        commit("SET_CALCULATED_PAYMENT_OPTIONS", sortedPaymentsAsc);
      } else {
        commit("SET_CALCULATED_PAYMENT_OPTIONS", []);
      }

      dispatch("setCalculatingEstimatedPayments", false);
    },
    async getSoftPullRatePlans({ state, commit }, applicantId: number) {
      const ratePlansResponse = await $api.refi.getSoftPullRatePlans(applicantId);

      if (ratePlansResponse?.ratePlans && ratePlansResponse?.ratePlans?.length > 0) {
        if (state.currentApplicantTypeId === 1) {
          commit("SET_PRIMARY_APPLICANT_SOFT_PULL_RATE_PLANS", ratePlansResponse?.ratePlans);
        }
        if (state.currentApplicantTypeId === 2) {
          commit("SET_CO_APPLICANT_SOFT_PULL_RATE_PLANS", ratePlansResponse?.ratePlans);
        }
      }
    },
  },
  getters: {
    loanMinNotMetError: (state: any): boolean => {
      const minimumLoanAmount = store.getters["PROGRAM_MINIMUM_LOAN_AMOUNT"];
      return state.estimatedLoanAmount >= 0 && state.estimatedLoanAmount < minimumLoanAmount;
    },
    ShowSamplePaymentCalculations: (state: any): any => {
      return (
        store.state.appData.currentState?.route === "refiShowMyRates" &&
        ((state.currentSubpageId === "VeryWellCreditQualified" && state.currentApplicantTypeId === 1) ||
          (state.currentSubpageId === "CreditQualified" && state.currentApplicantTypeId === 1) ||
          state.currentSubpageId === "SoftDeclineBorrowerLedSolo" ||
          state.currentSubpageId === "SoftDeclineBorrowerLedJoint") &&
        store.state.candidateRefiLoansModule?.candidateRefiLoans?.length === 0
      );
    },
    AvailableRatePlanOptions: (state: any): any => {
      const softPullRatePlans =
        state.currentApplicantTypeName === "Co-Applicant"
          ? state.coApplicantSoftPullRatePlans
          : state.primaryApplicantSoftPullRatePlans;
      if (softPullRatePlans && softPullRatePlans?.length > 0) {
        return softPullRatePlans.map((p: any) => {
          return {
            interestRate: p.rate,
            termInMonths: p.year * 12,
            termInYears: p.year,
            rateTypeName: p.rateTypeName ? p.rateTypeName : "",
            year: p.year,
            rate: p.rate,
          };
        });
      } else {
        return [];
      }
    },
    SortedAvailableRateOptions: (state: any): any => {
      const softPullRatePlans =
        state.currentApplicantTypeName === "Co-Applicant"
          ? state.coApplicantSoftPullRatePlans
          : state.primaryApplicantSoftPullRatePlans;
      if (softPullRatePlans && softPullRatePlans?.length > 0) {
        const availableRates = softPullRatePlans.map((p: any) => {
          return {
            interestRate: `${formatNumber(p.rate * 100)}%`,
            termInMonths: p.repaymentTerm,
            termInYears: p.repaymentTerm / 12,
            rateTypeName: p.rateTypeName,
          };
        });
        return groupBy(availableRates, "termInYears");
      } else {
        return [];
      }
    },
    SoftPullRatePlanOptions: (state: any): any => {
      const softPullRatePlans =
        state.currentApplicantTypeName === "Co-Applicant"
          ? state.coApplicantSoftPullRatePlans
          : state.primaryApplicantSoftPullRatePlans;
      return softPullRatePlans;
    },
    SoftPullAvailableTerms: (state: any): any => {
      const softPullRatePlans =
        state.currentApplicantTypeName === "Co-Applicant"
          ? state.coApplicantSoftPullRatePlans
          : state.primaryApplicantSoftPullRatePlans;

      return softPullRatePlans.map((rp: any) => rp.year);
    },
  },
};

export default showMyRatesModule;
